import { getApiBaseUrl } from '../../config/api';

const CONFIG_ERROR =
  'API base URL is not configured. Set REACT_APP_API_BASE_URL or uncomment API_BASE_URL in src/config/api.ts.';

/** Standard API JSON envelope for command endpoints (not OData feeds). */
export interface ApiErrorItem {
  code: number;
  message: string;
}

export interface ApiResultEnvelope {
  success: boolean;
  errors: ApiErrorItem[];
}

function isApiResultEnvelope(o: unknown): o is ApiResultEnvelope {
  if (!o || typeof o !== 'object') {
    return false;
  }
  const r = o as Record<string, unknown>;
  return typeof r.success === 'boolean' && Array.isArray(r.errors);
}

export function formatApiErrorsList(errors: unknown): string {
  if (!Array.isArray(errors) || errors.length === 0) {
    return 'Request failed.';
  }
  const parts: string[] = [];
  for (const item of errors) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const e = item as Record<string, unknown>;
    const message = typeof e.message === 'string' ? e.message : String(e.message ?? '');
    const codeRaw = e.code;
    const code =
      typeof codeRaw === 'number' && Number.isFinite(codeRaw)
        ? codeRaw
        : typeof codeRaw === 'string' && codeRaw.trim() !== '' && Number.isFinite(Number(codeRaw))
          ? Number(codeRaw)
          : undefined;
    if (code !== undefined) {
      parts.push(`[${code}] ${message}`.trim());
    } else if (message) {
      parts.push(message);
    }
  }
  return parts.length > 0 ? parts.join('; ') : 'Request failed.';
}

/**
 * When the JSON body uses `{ success, errors }`, throws if `success` is false.
 * No-op for other shapes (e.g. OData `{ value: [...] }`).
 */
export function assertApiSuccess(json: unknown): void {
  if (!isApiResultEnvelope(json)) {
    return;
  }
  if (json.success === true) {
    return;
  }
  throw new Error(formatApiErrorsList(json.errors));
}

export function requireApiBaseUrl(): string {
  const base = getApiBaseUrl();
  if (!base) {
    throw new Error(CONFIG_ERROR);
  }
  return base;
}

function messageFromParsedJson(j: Record<string, unknown>): string | null {
  if (isApiResultEnvelope(j)) {
    if (j.success === false) {
      return formatApiErrorsList(j.errors);
    }
    return null;
  }
  const title = typeof j.title === 'string' ? j.title : undefined;
  const detail = typeof j.detail === 'string' ? j.detail : undefined;
  const message = typeof j.message === 'string' ? j.message : undefined;
  const combined = [title, detail, message].filter(Boolean).join(' — ');
  if (combined) {
    return combined;
  }
  return null;
}

export async function getResponseErrorMessage(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) {
    return `Request failed with status ${response.status}`;
  }
  try {
    const j = JSON.parse(text) as Record<string, unknown>;
    const fromEnvelope = messageFromParsedJson(j);
    if (fromEnvelope) {
      return fromEnvelope;
    }
  } catch {
    /* not JSON */
  }
  return text.length > 280 ? `${text.slice(0, 280)}…` : text;
}

async function bearerAuthHeaders(
  getAccessToken: () => Promise<string | undefined>
): Promise<HeadersInit> {
  const token = await getAccessToken();
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export type ApiFetchInit = RequestInit & {
  /** Returns JWT (ID token) for `Authorization: Bearer …` when the user is signed in. */
  getAccessToken: () => Promise<string | undefined>;
};

export async function apiFetchJson<T>(path: string, init: ApiFetchInit): Promise<T> {
  const base = requireApiBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const { getAccessToken, headers: extra, ...rest } = init;
  const auth = await bearerAuthHeaders(getAccessToken);
  const response = await fetch(url, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...auth,
      ...extra,
    },
  });
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const ct = response.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    return undefined as T;
  }
  const parsed: unknown = await response.json();
  assertApiSuccess(parsed);
  return parsed as T;
}

export async function apiFetchVoid(path: string, init: ApiFetchInit): Promise<void> {
  const base = requireApiBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const { getAccessToken, headers: extra, ...rest } = init;
  const auth = await bearerAuthHeaders(getAccessToken);
  const response = await fetch(url, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...auth,
      ...extra,
    },
  });
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }
  const ct = response.headers.get('content-type') ?? '';
  if (response.status === 204) {
    return;
  }
  const text = await response.text();
  const trimmed = text.trim();
  if (!trimmed) {
    return;
  }
  if (ct.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed) as unknown;
    } catch {
      throw new Error('Invalid JSON in response.');
    }
    assertApiSuccess(parsed);
  }
}
